/**
 * Test data for UI development
 * Provides a complete story seed with all fields filled
 */

import type { StorySeed } from "@/types/story.ts";

export const TEST_STORY_DATA: StorySeed = {
  storyName: "The Curse of Skull Island",

  mainObjective: {
    description:
      "Find the legendary treasure of Captain Blackbeard hidden on Skull Island before the rival pirate crew does",
    skillUses: ["Navigation", "Treasure Hunting", "Sword Fighting", "Swimming"],
  },

  storyTwist:
    "The treasure map is actually a riddle that changes with the tides, and Captain Blackbeard's ghost appears at midnight to test worthy seekers",

  skills: [
    {
      name: "Sword Fighting",
      emoji: "⚔️",
      application: "Duel pirate rivals or battle ghost pirates in shipboard brawls",
    },
    {
      name: "Navigation",
      emoji: "🧭",
      application: "Read old maps and find shortcuts through treacherous waters",
    },
    {
      name: "Swimming Well",
      emoji: "🏊",
      application: "Dive for sunken clues or outswim pursuing sharks",
    },
    {
      name: "Treasure Hunting",
      emoji: "💎",
      application: "Decipher X-marks from faded maps or spot doubloons in wrecks",
    },
  ],

  characters: [
    {
      name: "Captain Saltbeard",
      role: "Grizzled pirate captain who knows the seas",
      emoji: "🏴‍☠️",
      skillUses: ["Navigation", "Intimidation", "Ship Steering"],
    },
    {
      name: "Marina the Mermaid",
      role: "Mysterious sea creature with hidden knowledge",
      emoji: "🧜‍♀️",
      skillUses: ["Swimming", "Treasure Hunting", "Animal Speaking"],
    },
    {
      name: "One-Eyed Jack",
      role: "Rival pirate captain seeking the same treasure",
      emoji: "👁️",
      skillUses: ["Sword Fighting", "Sneaking", "Intimidation"],
    },
    {
      name: "Polly the Parrot",
      role: "Talking bird with cryptic clues",
      emoji: "🦜",
      skillUses: ["Flying", "Animal Speaking", "Treasure Hunting"],
    },
    {
      name: "The Sea Witch",
      role: "Mysterious sorceress who guards the passage",
      emoji: "🔮",
      skillUses: ["Magic", "Swimming", "Intimidation"],
    },
  ],

  locations: [
    {
      name: "Skull Island Beach",
      description: "Sandy shores with palm trees and mysterious cave entrances",
      emoji: "🏝️",
      skillUses: ["Treasure Hunting", "Navigation", "Swimming"],
    },
    {
      name: "The Sunken Galleon",
      description: "Coral-crusted shipwreck deep beneath the waves with locked treasure chests",
      emoji: "🚢",
      skillUses: ["Swimming", "Treasure Hunting", "Rope Swinging"],
    },
    {
      name: "Pirate's Cove Tavern",
      description: "Rowdy waterfront bar where pirates trade stories and secrets",
      emoji: "🍺",
      skillUses: ["Intimidation", "Sneaking", "Sword Fighting"],
    },
    {
      name: "The Ghost Ship",
      description: "Phantom vessel that appears only at midnight in the fog",
      emoji: "👻",
      skillUses: ["Sword Fighting", "Navigation", "Swimming"],
    },
    {
      name: "Blackbeard's Cave",
      description: "Hidden grotto behind the waterfall, filled with traps and riddles",
      emoji: "⛰️",
      skillUses: ["Treasure Hunting", "Rope Swinging", "Swimming"],
    },
  ],

  sideQuests: [
    {
      description: "Convince the old sea witch to reveal the treasure's true location",
      skillUses: ["Intimidation", "Treasure Hunting"],
    },
    {
      description: "Win a ship race against One-Eyed Jack's fastest vessel",
      skillUses: ["Ship Steering", "Navigation"],
    },
    {
      description: "Retrieve Marina's stolen pearl necklace from the shark-infested reef",
      skillUses: ["Swimming", "Sword Fighting"],
    },
  ],

  encounters: [
    {
      description: "A sudden fog bank reveals a ghost ship drifting nearby",
      type: "event",
      skillUses: ["signal the crew in code", "track motion through thick fog"],
    },
    {
      description: "A mischievous monkey steals the map piece",
      type: "event",
      skillUses: ["chase across market rooftops", "set a clever fruit decoy"],
    },
    {
      description: "A hidden tide pool glitters with enchanted coins",
      type: "treasure",
      skillUses: ["spot fake coins by shimmer", "dive between sharp coral"],
    },
  ],

  storyIntro:
    "The salty sea breeze whips through your hair as your ship approaches the mysterious Skull Island. Legend says Captain Blackbeard buried his greatest treasure here centuries ago, but no one has ever returned to tell the tale. As the morning fog clears, you spot rival sails on the horizon - One-Eyed Jack is after the same prize!",
};
